import React from 'react';
import { Footprints, Trophy } from 'lucide-react';
import { usePedometer } from '../../hooks/usePedometer';
import { motion } from 'framer-motion';

interface PedometerWidgetProps {
  walletAddress: string | null;
}

export function PedometerWidget({ walletAddress }: PedometerWidgetProps) {
  const { steps, isAvailable, stepsToNextReward } = usePedometer(walletAddress);

  if (!isAvailable) {
    return (
      <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-lg p-6 backdrop-blur-sm border border-orange-500/20 mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Footprints className="text-orange-400" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-orange-100">Podomètre non disponible</h3>
            <p className="text-sm text-orange-200/70">
              Votre appareil ne supporte pas la détection des pas
            </p>
          </div>
        </div>
      </div>
    );
  }

  const progress = ((100 - stepsToNextReward) / 100) * 100;

  return (
    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-6 backdrop-blur-sm border border-green-500/20 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <motion.div 
          className="p-2 bg-green-500/20 rounded-lg"
          animate={{ rotate: [0, -10, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Footprints className="text-green-400" size={24} />
        </motion.div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-green-100">Podomètre</h3>
            <div className="flex items-center gap-2">
              <Trophy className="text-yellow-500" size={16} />
              <span className="text-sm text-yellow-300">
                50 coins / 100 pas
              </span>
            </div>
          </div>
          <p className="text-sm text-green-200/70">
            {steps.toLocaleString()} pas aujourd'hui
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-emerald-500"
            style={{ width: `${progress}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <div className="absolute inset-0 animate-pulse-subtle bg-white/10" />
          </motion.div>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-green-300">
            Prochain bonus dans {stepsToNextReward} pas
          </span>
          <span className="text-green-300">
            {Math.floor(steps / 100) * 50} coins gagnés
          </span>
        </div>
      </div>
    </div>
  );
}